import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { ArrowLeft, Search, Camera, ChevronRight, ChevronDown, MapPin } from 'lucide-react';
import { ConceptGroupPicker, filterByGroup, hasAreas, groupConcepts, SIN_AREA, SIN_PARTIDA } from '@/components/ConceptGroupPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CatalogoPage = () => {
  const navigate = useNavigate();
  const { selectedProject, concepts, setSelectedConcept } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState({ areas: new Set(), partidas: new Set() });
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  const [conceptStats, setConceptStats] = useState({});
  const [collapsedAreas, setCollapsedAreas] = useState({});

  useEffect(() => {
    // Redirect if no project selected
    if (!selectedProject) {
      navigate('/contratos');
      return;
    }

    // Fetch accumulated amounts for each concept
    fetchConceptStats();
  }, [selectedProject, navigate]);

  const fetchConceptStats = async () => {
    if (!selectedProject) return;
    
    try {
      // Get all figures for this project and calculate accumulated amounts
      const response = await axios.get(`${API}/figures`, {
        params: { project_id: selectedProject.id }
      });
      
      const figures = response.data || [];
      const stats = {};
      
      // Group figures by concept and calculate totals
      figures.forEach(fig => {
        if (!stats[fig.concept_id]) {
          stats[fig.concept_id] = { acumulado: 0, lastCapture: null };
        }
        if (fig.tipo === 'suma') {
          stats[fig.concept_id].acumulado += fig.cantidad || 0;
        } else {
          stats[fig.concept_id].acumulado -= fig.cantidad || 0;
        }
        
        const figDate = new Date(fig.created_at);
        if (!stats[fig.concept_id].lastCapture || figDate > stats[fig.concept_id].lastCapture) {
          stats[fig.concept_id].lastCapture = figDate;
        }
      });
      
      setConceptStats(stats);
    } catch (error) {
      console.error('Error fetching concept stats:', error);
    }
  };

  const handleSelectConcept = (concept) => {
    setSelectedConcept(concept);
    navigate('/capture');
  };

  const handleBack = () => {
    navigate('/contrato'); // Go back to contract detail
  };

  // Filter concepts by search term
  const groupedCatalog = hasAreas(concepts);
  const activeGroupFilters = groupFilter.areas.size + groupFilter.partidas.size;
  const filteredConcepts = filterByGroup(concepts, groupFilter).filter(c => 
    c.clave.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get progress color based on percentage
  const getProgressColor = (avance) => {
    if (avance > 100) return 'bg-[#FF6B00]'; // Over 100%
    if (avance >= 100) return 'bg-green-500';
    if (avance >= 75) return 'bg-[#0066FF]';
    if (avance >= 50) return 'bg-[#00CFFF]';
    if (avance >= 25) return 'bg-[#1E3550]';
    return 'bg-[#1E3550]';
  };

  // Get text color for percentage
  const getProgressTextColor = (avance) => {
    if (avance > 100) return 'text-[#FF6B00] font-bold';
    return 'text-[#E2EAF4]';
  };

  const renderRow = (concept, index) => {
    const stats = conceptStats[concept.id] || { acumulado: 0, lastCapture: null };
    const avance = concept.contratado > 0 ? (stats.acumulado / concept.contratado) * 100 : 0;
    const barWidth = Math.min(100, avance);
    const rowBg = index % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]';
    return (
      <div
        key={concept.id}
        onClick={() => handleSelectConcept(concept)}
        className={`grid grid-cols-12 gap-2 px-4 py-4 hover:bg-[#1E3550]/50 cursor-pointer transition-colors items-center ${rowBg}`}
        data-testid={`concept-row-${concept.id}`}
      >
        <div className="col-span-1">
          <span className="font-bold text-[#00CFFF] text-sm">{concept.clave}</span>
          {!groupedCatalog && (concept.area || concept.partida) && (
            <span className="block text-[9px] text-[#5A7A9A] leading-tight" data-testid={`concept-group-${concept.id}`}>{[concept.area, concept.partida].filter(Boolean).join(' · ')}</span>
          )}
          {concept.local && (
            <span className="block w-fit mt-0.5 rounded-full bg-[#FF6B00]/15 px-1.5 text-[9px] font-semibold text-[#FF6B00]" title="Pendiente de sincronizar" data-testid={`concept-local-badge-${concept.id}`}>
              local
            </span>
          )}
        </div>
        <div className="col-span-4">
          <p className="text-sm text-[#E2EAF4] line-clamp-2">{concept.descripcion}</p>
        </div>
        <div className="col-span-1 text-center">
          <span className="text-xs font-medium text-[#00CFFF] bg-[#0066FF]/20 px-2 py-1 rounded border border-[#0066FF]/30">
            {concept.unidad}
          </span>
        </div>
        <div className="col-span-2 text-right">
          <span className="font-mono text-sm text-[#E2EAF4]">{concept.contratado.toFixed(2)}</span>
        </div>
        <div className="col-span-2 text-right">
          <span className={`font-mono text-sm font-semibold ${stats.acumulado > 0 ? 'text-green-400' : 'text-[#5A7A9A]'}`}>
            {stats.acumulado.toFixed(2)}
          </span>
        </div>
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-[#1E3550] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${getProgressColor(avance)}`} style={{ width: `${barWidth}%` }} />
            </div>
            <span className={`text-xs font-semibold w-14 text-right ${getProgressTextColor(avance)}`}>
              {avance.toFixed(1)}%
            </span>
            <ChevronRight className="w-4 h-4 text-[#5A7A9A]" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#060C18] flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-[#E2EAF4]">{selectedProject?.name}</h1>
              <p className="text-[#5A7A9A] text-sm">Catálogo de Conceptos</p>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-[#0D1520] border-b border-[#1E3550] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A7A9A]" />
            <Input
              type="text"
              placeholder="Buscar por clave o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
              data-testid="search-concepts"
            />
          </div>
          {groupedCatalog && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowGroupFilter((v) => !v)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1 ${activeGroupFilters ? 'border-[#00CFFF] text-[#00CFFF] bg-[#00CFFF]/10' : 'border-[#1E3550] text-[#5A7A9A] hover:text-[#E2EAF4]'}`}
                data-testid="btn-toggle-group-filter"
              >
                <MapPin className="w-3.5 h-3.5" />
                {activeGroupFilters ? `Filtrando por área/partida (${activeGroupFilters})` : 'Filtrar por área / partida'}
              </button>
              {activeGroupFilters > 0 && (
                <button type="button" onClick={() => setGroupFilter({ areas: new Set(), partidas: new Set() })} className="ml-2 text-xs text-[#5A7A9A] hover:text-[#E2EAF4]" data-testid="btn-clear-group-filter">Quitar filtro</button>
              )}
              {showGroupFilter && (
                <div className="mt-2">
                  <ConceptGroupPicker concepts={concepts} value={groupFilter} onChange={setGroupFilter} compact />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Concepts Table */}
      <main className="flex-1 max-w-6xl mx-auto px-4 py-4 w-full">
        <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0A1422] border-b border-[#1E3550] text-xs font-semibold text-[#5A7A9A] uppercase">
            <div className="col-span-1">Clave</div>
            <div className="col-span-4">Descripción</div>
            <div className="col-span-1 text-center">Unidad</div>
            <div className="col-span-2 text-right">Contratado</div>
            <div className="col-span-2 text-right">Acumulado</div>
            <div className="col-span-2 text-center">Avance</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-[#1E3550]" data-testid="concepts-list">
            {!groupedCatalog && filteredConcepts.map((concept, index) => renderRow(concept, index))}

            {groupedCatalog && Object.entries(groupConcepts(filteredConcepts)).map(([area, byPartida]) => {
              const areaConcepts = Object.values(byPartida).flat();
              const isCollapsed = !!collapsedAreas[area];
              const partidaKeys = Object.keys(byPartida);
              const showPartidas = partidaKeys.length > 1 || partidaKeys[0] !== SIN_PARTIDA;
              let rowIndex = 0;
              return (
                <React.Fragment key={area}>
                  <div
                    onClick={() => setCollapsedAreas((o) => ({ ...o, [area]: !isCollapsed }))}
                    className="flex items-center gap-2 px-4 py-3 bg-[#0D1B2E] cursor-pointer select-none hover:bg-[#12233B]"
                    data-testid={`catalogo-area-row-${area}`}
                  >
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-[#5A7A9A]" /> : <ChevronDown className="w-4 h-4 text-[#5A7A9A]" />}
                    <MapPin className="w-4 h-4 text-[#00CFFF]" />
                    <span className="font-semibold text-sm text-[#E2EAF4]">
                      {area === SIN_AREA ? <span className="text-[#5A7A9A] italic">{area}</span> : area}
                    </span>
                    <span className="text-[10px] font-normal text-[#5A7A9A]">{areaConcepts.length} concepto{areaConcepts.length !== 1 ? 's' : ''}</span>
                  </div>
                  {!isCollapsed && partidaKeys.map((partida) => (
                    <React.Fragment key={`${area}-${partida}`}>
                      {showPartidas && (
                        <div className="px-4 py-1.5 pl-10 bg-[#0A1422] text-[11px] font-semibold uppercase tracking-wide text-[#A855F7]" data-testid={`catalogo-partida-row-${area}-${partida}`}>
                          {partida === SIN_PARTIDA ? <span className="text-[#5A7A9A] italic normal-case">{partida}</span> : partida}
                          <span className="ml-2 text-[10px] font-normal text-[#5A7A9A] normal-case">{byPartida[partida].length}</span>
                        </div>
                      )}
                      {byPartida[partida].map((concept) => renderRow(concept, rowIndex++))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}

            {filteredConcepts.length === 0 && (
              <div className="text-center py-12">
                <Camera className="w-12 h-12 text-[#1E3550] mx-auto mb-3" />
                <p className="text-[#5A7A9A]">
                  {searchTerm ? 'No se encontraron conceptos' : 'No hay conceptos en este contrato'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Footer */}
        <div className="mt-4 bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
          <div className="flex justify-between items-center text-sm">
            <span className="text-[#5A7A9A]">
              Total de conceptos: <span className="font-semibold text-[#E2EAF4]">{filteredConcepts.length}</span>
            </span>
            <span className="text-[#5A7A9A]">
              Toca un concepto para comenzar a capturar
            </span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CatalogoPage;
